// pages/TraductionsIncompletes.jsx
// Page de gestion des traductions incomplètes

import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Languages, Search, ArrowLeft, Sparkles, Loader2 } from 'lucide-react';
import GestionMots from '../components/GestionMots';
import './TraductionsIncompletes.css';

export default function TraductionsIncompletes() {
  const location = useLocation();
  const [showResults, setShowResults] = useState(false);
  const [langueSource, setLangueSource] = useState('fr');
  const [langueCible, setLangueCible] = useState('es');
  const [mots, setMots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [contextes, setContextes] = useState([]);
  const [enriching, setEnriching] = useState(false);
  const [enrichProgress, setEnrichProgress] = useState({ done: 0, total: 0 });
  const [batchSize, setBatchSize] = useState('10'); // '10', '50', ou 'tout'

  // Réinitialiser l'écran 1 quand on navigue vers cette page depuis le menu
  useEffect(() => {
    if (location.state?.resetToScreen1) {
      setShowResults(false);
    }
  }, [location.state]);

  // Charger les contextes au montage
  useEffect(() => {
    loadContextes();
  }, []);

  // Réinitialiser langueCible si elle devient identique à langueSource
  useEffect(() => {
    if (langueSource === langueCible) {
      // Trouver la première langue différente de langueSource
      const autreLangue = langueOptions.find(opt => opt.value !== langueSource);
      if (autreLangue) {
        setLangueCible(autreLangue.value);
      }
    }
  }, [langueSource, langueCible]);

  // Charger la liste des contextes prédéfinis
  const loadContextes = async () => {
    try {
      const response = await fetch('/api/contextes');
      const data = await response.json();

      if (data.success) {
        const contextesTries = [...(data.contextes || [])].sort((a, b) => {
          if (a.value === '') return -1;
          if (b.value === '') return 1;
          return a.label.localeCompare(b.label, 'fr');
        });
        setContextes(contextesTries);
      }
    } catch (err) {
      console.error('Erreur chargement contextes:', err);
    }
  };

  // Rechercher les traductions incomplètes
  const handleRechercher = async () => {
    if (langueSource === langueCible) {
      alert('❌ La langue source et la langue cible doivent être différentes');
      return;
    }

    setLoading(true);
    setError(null);
    setShowResults(true);

    console.log(`🔍 [TraductionsIncompletes] Recherche: ${langueSource} → ${langueCible}`);

    try {
      const response = await fetch(
        `/api/mots/traductions-incompletes?langueSource=${langueSource}&langueCible=${langueCible}`
      );
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erreur lors de la recherche');
      }

      console.log(`✅ [TraductionsIncompletes] Résultat: ${data.mots.length} mots trouvés`);
      console.log(`  - Mots:`, data.mots.map(m => m.motSingulier).join(', '));

      setMots(data.mots || []);
    } catch (err) {
      console.error('Erreur recherche traductions incomplètes:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Recharger les résultats
  const handleReload = () => {
    handleRechercher();
  };

  // Enrichir avec l'IA (batch)
  const handleEnrichirIA = async () => {
    if (mots.length === 0) {
      alert('Aucun mot a enrichir');
      return;
    }

    // Déterminer le nombre de mots à traiter
    const nombreATraiter = batchSize === 'tout' ? mots.length : Math.min(parseInt(batchSize), mots.length);

    const confirm = window.confirm(
      `Enrichir ${nombreATraiter} mot(s) avec l'IA ?\n\nCela va generer automatiquement les traductions vers ${langueOptions.find(l => l.value === langueCible)?.label}.\n\nCette operation utilise l'API Claude et peut prendre quelques instants.`
    );

    if (!confirm) return;

    setEnriching(true);
    setEnrichProgress({ done: 0, total: nombreATraiter });

    // Ne prendre que les N premiers mots selon la sélection
    const motIds = mots.slice(0, nombreATraiter).map(m => m.id);
    let processed = 0;
    let errors = [];

    // Traiter par batch de 10
    while (processed < motIds.length) {
      const batch = motIds.slice(processed, processed + 10);

      try {
        const response = await fetch('/api/enrichment/translate-batch', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            motIds: batch,
            langueCible: langueCible,
          }),
        });

        const data = await response.json();

        if (data.success) {
          processed += batch.length;
          setEnrichProgress({ done: processed, total: motIds.length });

          if (data.results.errors.length > 0) {
            errors = [...errors, ...data.results.errors];
          }
        } else {
          throw new Error(data.error);
        }
      } catch (err) {
        console.error('Erreur enrichissement batch:', err);
        errors.push({ error: err.message });
        processed += batch.length;
      }
    }

    setEnriching(false);

    if (errors.length > 0) {
      alert(`Enrichissement termine avec ${errors.length} erreur(s).\n\nLa liste va etre actualisee.`);
    } else {
      alert('Enrichissement termine avec succes !\n\nLa liste va etre actualisee.');
    }

    // Recharger la liste
    handleRechercher();
  };

  // Retour à l'écran de recherche
  const handleRetourRecherche = () => {
    setShowResults(false);
    setMots([]);
    setError(null);
  };

  // Options de langues
  const langueOptions = [
    { value: 'fr', label: '🇫🇷 Français' },
    { value: 'es', label: '🇪🇸 Espagnol' },
    { value: 'en', label: '🇬🇧 Anglais' },
  ];

  return (
    <div className="traductions-incompletes-page">
      {/* Écran de sélection */}
      {!showResults && (
        <div className="selection-screen">
          <div className="page-header">
            <h1>
              <Languages size={32} color="#F59E0B" />
              Traductions Incomplètes
            </h1>
            <p className="page-description">
              Trouvez les mots qui n'ont pas de traduction vers une langue spécifique
            </p>
          </div>

          <div className="selection-form">
            <div className="form-group">
              <label htmlFor="langue-source-select">📤 Langue source</label>
              <select
                id="langue-source-select"
                value={langueSource}
                onChange={(e) => setLangueSource(e.target.value)}
                className="form-select"
              >
                {langueOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="arrow-separator">
              →
            </div>

            <div className="form-group">
              <label htmlFor="langue-cible-select">📥 Langue destination</label>
              <select
                id="langue-cible-select"
                value={langueCible}
                onChange={(e) => setLangueCible(e.target.value)}
                className="form-select"
              >
                {langueOptions
                  .filter((opt) => opt.value !== langueSource)
                  .map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
              </select>
            </div>

            <button
              onClick={handleRechercher}
              disabled={langueSource === langueCible}
              className="btn-rechercher"
            >
              <Search size={20} />
              Rechercher
            </button>

            {langueSource === langueCible && (
              <p className="form-warning">
                ⚠️ Veuillez choisir deux langues différentes
              </p>
            )}
          </div>
        </div>
      )}

      {/* Écran de résultats */}
      {showResults && (
        <div className="resultats-screen">
          <div className="page-header">
            <div className="header-with-back">
              <button onClick={handleRetourRecherche} className="btn-back">
                <ArrowLeft size={20} />
                Nouvelle recherche
              </button>
              <h1>
                <Languages size={32} color="#F59E0B" />
                Mots sans traduction
              </h1>
            </div>
            <p className="page-description">
              Mots en {langueOptions.find(l => l.value === langueSource)?.label} sans traduction vers {langueOptions.find(l => l.value === langueCible)?.label}
            </p>

            {/* Bouton enrichissement IA */}
            {mots.length > 0 && (
              <div className="enrichment-bar">
                <span className="enrichment-count">{mots.length} mot(s) a traduire</span>
                <select
                  value={batchSize}
                  onChange={(e) => setBatchSize(e.target.value)}
                  disabled={enriching}
                  className="batch-size-select"
                >
                  <option value="10">10</option>
                  <option value="50">50</option>
                  <option value="tout">Tout</option>
                </select>
                <button
                  onClick={handleEnrichirIA}
                  disabled={enriching}
                  className="btn-enrichir-ia"
                >
                  {enriching ? (
                    <>
                      <Loader2 size={18} className="spinning" />
                      Enrichissement... ({enrichProgress.done}/{enrichProgress.total})
                    </>
                  ) : (
                    <>
                      <Sparkles size={18} />
                      Enrichir avec l'IA
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          <GestionMots
            mots={mots}
            loading={loading}
            error={error}
            onReload={handleReload}
            showValiderButton={false}
            contextes={contextes}
            emptyMessage="Tous les mots ont une traduction ! 🎉"
            emptyHint="Bravo, votre vocabulaire est complet pour cette combinaison de langues."
            langueCibleManquante={langueCible}
          />
        </div>
      )}
    </div>
  );
}
